import React, { FC, ReactElement, useContext, useEffect } from 'react'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../Common/Contexts'
import { LanguageErrorCodes, TimezoneModal } from '../../../../i18n'
import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { FormProvider, UseFormMethods } from '../../../Vendor/react-hook-form/dist/index.ie11'
import { HIDE } from '../../../NonReact/constants/class-names'
import FooterActions from '../../Common/Components/FooterActions/FooterActions'
import { ModalUtilsContextType } from '../../Common/Contexts/ModalUtilsContext'
import { useModalUtilsContext } from '../../Common/Hooks/useModalUtilsContext'
import { TimezoneFormData } from '../Types/Types'
import { MainTab } from '../Views/Main'
import { UserInfo, useUserInfo } from '../../../ReactRouting/Hooks/useUserInfo'
import { IsVarEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import MessageContext, { MessageContextType } from '../../Common/Contexts/MessageContext'
import { DEFAULT_TIMEZONE } from '../Constants/Constants'
import { MapToFormState } from '../Mappers/MapToFormState'
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { userInfoURL } from '../../../NonReact/constants/ApiConstants'
import { UserSettings, useUserSettings } from '../../Common/Hooks/useUserSettings'
import { getUserSettings, updateUserSettings } from '../../../NonReact/common/API/EntityAPIClient'
import {
    ApplicationResponseNotification,
    CurrentUserResponse,
    JSONResponse
} from '../../../NonReact/common/Types/Types'

type Props = {
    closeModal: () => void
    methods: UseFormMethods
}

export const TimezoneContent: FC<Props> = ({ closeModal, methods }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(LanguageErrorCodes.NAME)
    language.loadBundle(TimezoneModal.NAME)

    const [userInfo]: UserInfo = useUserInfo()
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setUserSettings]: UserSettings = useUserSettings()
    const { displayError }: MessageContextType = useContext(MessageContext)

    const { handleSubmit, reset }: UseFormMethods = methods

    const { serverError, isValidationPopperOpen, invalidFormSubmit, isSubmitting }: ModalUtilsContextType =
        useModalUtilsContext()

    const submitTimezoneConfiguration = (state: TimezoneFormData): void => {
        getUserSettings().then((settings: JSONResponse<ApplicationResponseNotification>): void => {
            if (isVarNotEmpty(settings)) {
                updateUserSettings({
                    ...settings,
                    TimeZone: state.timezone === DEFAULT_TIMEZONE ? null : state.timezone
                })
                    .then((userSettings: JSONResponse<ApplicationResponseNotification>): void => {
                        if (isVarNotEmpty(userSettings)) {
                            setUserSettings(userSettings)
                        }
                    })
                    .catch(displayError)
                    .finally((): void => {
                        closeModal()
                    })
            }
        })
    }

    useEffect((): void => {
        requestGetJSON<CurrentUserResponse>(userInfoURL)
            .then((user: JSONResponse<CurrentUserResponse>): void => {
                if (IsVarEmpty(user?.Data?.TimeZone)) return
                reset(MapToFormState(user?.Data.TimeZone))
            })
            .catch(displayError)
    }, [JSON.stringify(userInfo)])

    return (
        <div className='configuration-tabs'>
            <FormProvider {...methods}>
                <form
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(submitTimezoneConfiguration, invalidFormSubmit)}
                    className={isSubmitting ? HIDE : EMPTY_STRING}
                    noValidate
                >
                    <MainTab />
                    <FooterActions
                        serverError={serverError}
                        isValidationPopperOpen={isValidationPopperOpen}
                        closeModal={closeModal}
                        showHelpButton={false}
                    />
                </form>
            </FormProvider>
        </div>
    )
}
